'use client';

import { useEffect } from 'react';
import { useAudio, type PodcastEpisode } from '@/components/providers/AudioProvider';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
  episodes: PodcastEpisode[];
}

export default function MarqueePodPlayer({ episodes }: Props) {
  const { currentEpisode, playing, play, togglePlay, setPlaylist } = useAudio();

  useEffect(() => {
    if (episodes.length > 0) {
      setPlaylist(episodes);
    }
  }, [episodes, setPlaylist]);

  const handlePlayEpisode = (episode: PodcastEpisode, index: number) => {
    setPlaylist(episodes, index);
    play(episode);
  };

  return (
    <div className="marquee-pod-player">
      {episodes.length > 0 && (
        <div className="marquee-pod-featured">
          <button
            className="marquee-pod-featured-play"
            onClick={() => handlePlayEpisode(episodes[0], 0)}
            aria-label={
              currentEpisode?.id === episodes[0].id && playing
                ? 'Pause'
                : 'Play latest episode'
            }
          >
            {currentEpisode?.id === episodes[0].id && playing ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <div className="marquee-pod-featured-info">
            <div className="marquee-pod-featured-label">Latest Episode</div>
            <div className="marquee-pod-featured-date">
              {formatDate(episodes[0].episode_date)}
              {episodes[0].duration_seconds
                ? ` · ${formatDuration(episodes[0].duration_seconds)}`
                : ''}
            </div>
          </div>
        </div>
      )}

      <div className="marquee-pod-list">
        {episodes.map((ep, i) => {
          const isActive = currentEpisode?.id === ep.id;
          return (
            <button
              key={ep.id}
              className={`marquee-pod-item${isActive ? ' active' : ''}`}
              onClick={() => {
                if (isActive) {
                  togglePlay();
                } else {
                  handlePlayEpisode(ep, i);
                }
              }}
            >
              <span className="marquee-pod-item-icon">
                {isActive && playing ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </span>
              <span className="marquee-pod-item-date">{formatDate(ep.episode_date)}</span>
              <span className="marquee-pod-item-duration">
                {formatDuration(ep.duration_seconds)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
